import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { clsx } from 'clsx';
import { ChangeEvent, ReactElement, ReactNode, useCallback } from 'react';
import { InputSize, InputStateVariant } from '../InputProps.types';

import styles from './radio.module.scss';

export type RadioGroupProps = {
  children?: ReactNode;

  name?: string;
  id?: string;
  className?: string;
  state?: InputStateVariant;
  size?: InputSize;

  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
} & Pick<
  RadixRadioGroup.RadioGroupProps,
  | 'defaultValue'
  | 'value'
  | 'onValueChange'
  | 'disabled'
  | 'required'
  | 'orientation'
> &
  HTMLDataAttributes;

/**
 * RadioGroup component
 *
 * @see https://www.radix-ui.com/primitives/docs/components/radio-group
 */
export const RadioGroup = ({
  children,
  name = 'radio-group',
  className,
  state,
  size = 'l',
  orientation = 'vertical',
  onValueChange,
  onChange,
  ...props
}: RadioGroupProps): ReactElement => {
  const _onValueChange = useCallback(
    (value: string) => {
      onValueChange?.(value);

      onChange?.({
        // RHF
        target: { name, value },
      } as unknown as ChangeEvent<HTMLInputElement>);
    },
    [name, onChange, onValueChange]
  );

  return (
    <RadixRadioGroup.Root
      className={clsx(styles.radioGroup, className)}
      data-input-state={state}
      data-size={size}
      name={name}
      orientation={orientation}
      onValueChange={_onValueChange}
      {...props}
    >
      {children}
    </RadixRadioGroup.Root>
  );
};
